import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createUpload } from './api'
import { useDirectUpload } from './useDirectUpload'
import { useUploadPoller } from './useUploadPoller'

export const MAX_UPLOAD_FILE_SIZE = 100 * 1024 * 1024
export const MAX_BUILDER_IMAGES = 50
export const MAX_BUILDER_TOTAL_BYTES = 20 * MAX_UPLOAD_FILE_SIZE
export const ACCEPTED_UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/webp']

export const ARTWORK_COUNT_LIMIT_ERROR = `This gang sheet can include up to ${MAX_BUILDER_IMAGES} artwork files. Remove a design or finish this gang sheet and start another.`
export const ARTWORK_TOTAL_SIZE_LIMIT_ERROR = 'These files would exceed the 2 GB upload limit for one gang sheet. Remove a design, choose smaller files, or finish this gang sheet and start another.'

function imageByteSize(image) {
  const byteSize = Number(image?.byteSize ?? image?.byte_size)
  return Number.isFinite(byteSize) && byteSize > 0 ? byteSize : 0
}

function totalImageBytes(images) {
  return (images || []).reduce((total, image) => total + imageByteSize(image), 0)
}

export function useBuilderUploads({
  vendorToken,
  builderSessionId,
  builderDraftPublicId,
  uploadScopeKey = builderDraftPublicId,
  images,
  onImagesChange,
}) {
  const [uploadingState, setUploadingState] = useState({ generation: null, files: [] })
  const [errorState, setErrorState] = useState({ generation: null, errors: [] })
  const imagesRef = useRef(images || [])
  const mountedRef = useRef(false)
  const scopeKeyRef = useRef(uploadScopeKey)
  const scopeGenerationRef = useRef(0)
  const reservedSlotsRef = useRef(0)
  const reservedBytesRef = useRef(0)
  const nextTemporaryIdRef = useRef(0)
  const scopeAbortRef = useRef(new AbortController())

  if (scopeKeyRef.current !== uploadScopeKey) {
    scopeAbortRef.current.abort()
    scopeAbortRef.current = new AbortController()
    scopeKeyRef.current = uploadScopeKey
    scopeGenerationRef.current += 1
    reservedSlotsRef.current = 0
    reservedBytesRef.current = 0
  }

  imagesRef.current = images || []
  const { uploadFile } = useDirectUpload(vendorToken, builderSessionId, builderDraftPublicId)

  useEffect(() => {
    if (scopeAbortRef.current.signal.aborted) scopeAbortRef.current = new AbortController()
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      scopeAbortRef.current.abort()
      scopeGenerationRef.current += 1
      reservedSlotsRef.current = 0
      reservedBytesRef.current = 0
    }
  }, [])

  const pendingIds = useMemo(() => (
    uploadScopeKey ? (images || [])
      .filter(image => (
        image.processingStatus === 'pending' ||
        image.backgroundProcessingStatus === 'processing'
      ))
      .map(image => image.id)
      : []
  ), [images, uploadScopeKey])
  const uploadProcessingIds = useMemo(() => (
    uploadScopeKey ? (images || [])
      .filter(image => image.processingStatus === 'pending')
      .map(image => image.id)
      : []
  ), [images, uploadScopeKey])

  const updateImages = useCallback((updater) => {
    imagesRef.current = updater(imagesRef.current)
    onImagesChange(previousImages => updater(previousImages))
  }, [onImagesChange])

  const handleStatusUpdate = useCallback((updates, type) => {
    if (type === 'timeout') {
      const timedOutIds = new Set(updates.map(String))
      updateImages(previousImages => previousImages.map(image => (
        timedOutIds.has(String(image.id))
          ? {
              ...image,
              ...(image.processingStatus === 'pending'
                ? { processingStatus: 'failed', processingError: 'processing_error' }
                : {}),
            }
          : image
      )))
      return
    }

    const updatesById = new Map(updates.map(update => [String(update.id), update]))
    updateImages(previousImages => previousImages.map(image => {
      const update = updatesById.get(String(image.id))
      if (!update) return image

      const currentBackgroundRevision = Number(image.backgroundRevision ?? 0)
      const updateBackgroundRevision = Number(update.backgroundRevision ?? currentBackgroundRevision)
      if (updateBackgroundRevision < currentBackgroundRevision) return image

      const backgroundChanged = (
        update.backgroundRevision !== undefined &&
        updateBackgroundRevision !== currentBackgroundRevision
      )
      return {
        ...image,
        ...update,
        ...(backgroundChanged ? { imageUrl: null, previewUrl: null } : {}),
      }
    }))
  }, [updateImages])

  useUploadPoller(
    vendorToken,
    builderSessionId,
    pendingIds,
    handleStatusUpdate,
    builderDraftPublicId,
    uploadProcessingIds
  )

  const uploadFiles = useCallback(async (files) => {
    if (!uploadScopeKey || !files?.length) return

    const newErrors = []
    const generation = scopeGenerationRef.current
    const scopeKey = uploadScopeKey
    const isCurrentScope = () => (
      mountedRef.current &&
      scopeGenerationRef.current === generation &&
      scopeKeyRef.current === scopeKey
    )

    for (const file of Array.from(files)) {
      if (!isCurrentScope()) return

      if (!ACCEPTED_UPLOAD_TYPES.includes(file.type)) {
        newErrors.push(`${file.name}: unsupported file type`)
        continue
      }
      if (file.size > MAX_UPLOAD_FILE_SIZE) {
        newErrors.push(`${file.name}: file exceeds 100 MB limit`)
        continue
      }
      if (imagesRef.current.length + reservedSlotsRef.current >= MAX_BUILDER_IMAGES) {
        newErrors.push(ARTWORK_COUNT_LIMIT_ERROR)
        break
      }
      if (totalImageBytes(imagesRef.current) + reservedBytesRef.current + file.size > MAX_BUILDER_TOTAL_BYTES) {
        newErrors.push(ARTWORK_TOTAL_SIZE_LIMIT_ERROR)
        continue
      }

      reservedSlotsRef.current += 1
      reservedBytesRef.current += file.size
      nextTemporaryIdRef.current += 1
      const tempId = `uploading-${generation}-${nextTemporaryIdRef.current}`
      setUploadingState(previous => ({
        generation,
        files: [
          ...(previous.generation === generation ? previous.files : []),
          { tempId, filename: file.name, progress: 0 },
        ],
      }))

      try {
        const progress = (progress) => {
          if (!isCurrentScope()) return
          setUploadingState(previous => ({
            generation,
            files: (previous.generation === generation ? previous.files : []).map(uploadingFile => (
              uploadingFile.tempId === tempId ? { ...uploadingFile, progress } : uploadingFile
            )),
          }))
        }
        let image
        let blob
        try {
          blob = await uploadFile(file, { onProgress: progress })
        } catch (error) {
          if (!isCurrentScope()) return
          // Retry exactly once through the app only when the browser could not
          // reach storage. Presign denials and storage HTTP errors stay errors.
          if (error.uploadPhase !== 'storage' || error.storageStatus !== 0) throw error
          progress(null)
          image = await createUpload(vendorToken, builderSessionId, file, builderDraftPublicId, {
            signal: scopeAbortRef.current.signal,
          })
          progress(100)
        }
        if (!isCurrentScope()) return

        image ||= builderDraftPublicId
          ? await createUpload(
              vendorToken,
              builderSessionId,
              blob.signed_id,
              builderDraftPublicId
            )
          : await createUpload(vendorToken, builderSessionId, blob.signed_id)
        if (!isCurrentScope()) return

        updateImages(previousImages => [
          ...previousImages,
          imageByteSize(image) > 0 ? image : { ...image, byteSize: file.size },
        ])
      } catch (error) {
        if (isCurrentScope()) {
          newErrors.push(`${file.name}: ${error?.message || 'upload failed'}`)
        }
      } finally {
        if (isCurrentScope()) {
          reservedSlotsRef.current = Math.max(0, reservedSlotsRef.current - 1)
          reservedBytesRef.current = Math.max(0, reservedBytesRef.current - file.size)
          setUploadingState(previous => ({
            generation,
            files: (previous.generation === generation ? previous.files : [])
              .filter(uploadingFile => uploadingFile.tempId !== tempId),
          }))
        }
      }
    }

    if (newErrors.length > 0 && isCurrentScope()) {
      setErrorState(previous => ({
        generation,
        errors: [...new Set([
          ...(previous.generation === generation ? previous.errors : []),
          ...newErrors,
        ])],
      }))
    }
  }, [
    builderDraftPublicId,
    builderSessionId,
    updateImages,
    uploadScopeKey,
    uploadFile,
    vendorToken,
  ])

  const currentGeneration = scopeGenerationRef.current
  const uploadingFiles = uploadingState.generation === currentGeneration ? uploadingState.files : []
  const uploadErrors = errorState.generation === currentGeneration ? errorState.errors : []

  return {
    uploadFiles,
    uploadingFiles,
    uploadErrors,
    uploadsActive: uploadingFiles.length > 0 || pendingIds.length > 0,
  }
}
